import { ReactNode, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { Trans, useTranslation } from "react-i18next"

import { useTimezone } from "hooks/useTimezone"

import { BetbyBonusService, BetbyDbTemplate } from "services/betbyBonus/betbyBonus.service"

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

/** Coarse "time since" label — the largest non-zero unit wins. */
const relativeTimeLabel = (t: TranslateFn, iso: string): string => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return t("betbyBonuses.templates.daysAgo", { value: days })
  if (hours > 0) return t("betbyBonuses.templates.hoursAgo", { value: hours })
  if (minutes > 0) return t("betbyBonuses.templates.minutesAgo", { value: minutes })
  return t("betbyBonuses.templates.justNow")
}

function RelativeTime({ iso }: { iso: string | null }) {
  const { t } = useTranslation("translation", { useSuspense: false })
  if (!iso) return <span className="text-gray-400 text-xs">{t("betbyBonuses.templates.never")}</span>
  return <span className="text-xs text-gray-500">{relativeTimeLabel(t, iso)}</span>
}

function DateRange({ from, to }: { from: string | null; to: string | null }) {
  const { formatDate } = useTimezone()
  const fmt = (iso: string) => formatDate(iso)
  if (!from && !to) return <span className="text-gray-400 text-xs">—</span>
  return (
    <span className="text-xs text-gray-500 whitespace-nowrap">
      {from ? fmt(from) : "?"} → {to ? fmt(to) : "?"}
    </span>
  )
}

const freebetTypeLabel = (t: TranslateFn, type: string): string => {
  switch (type) {
    case "free_money":
      return t("betbyBonuses.templates.freebetTypes.freeMoney")
    case "snr":
      return t("betbyBonuses.templates.freebetTypes.snr")
    case "bet_refund":
      return t("betbyBonuses.templates.freebetTypes.betRefund")
    default:
      return type
  }
}

const getDescription = (template: BetbyDbTemplate): string | null =>
  template.descriptions?.en ?? template.descriptions?.[Object.keys(template.descriptions ?? {})[0]] ?? null

function CopyBadge({ value }: { value: string }) {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      onClick={handleCopy}
      title={t("betbyBonuses.templates.copyId")}
      className="font-mono text-xs text-gray-400 hover:text-blue-400 transition-colors"
    >
      {copied ? t("betbyBonuses.templates.copied") : value}
    </button>
  )
}

function DetailsModal({ template, onClose }: { template: BetbyDbTemplate; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b dark:border-gray-700">
          <span className="font-semibold text-gray-900 dark:text-white">{template.name}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">
            &times;
          </button>
        </div>
        <div className="overflow-auto p-5 flex-1">
          <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
            {JSON.stringify(template.meta ?? template, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  )
}

type TemplateColumn = {
  key: string
  label: string
  cellClassName?: string
  title?: (template: BetbyDbTemplate) => string | undefined
  render: (template: BetbyDbTemplate) => ReactNode
}

const buildColumns = (t: TranslateFn, onShowDetails: (template: BetbyDbTemplate) => void): TemplateColumn[] => [
  {
    key: "name",
    label: t("betbyBonuses.templates.columns.name"),
    cellClassName: "font-medium text-gray-900 dark:text-white",
    render: (template) => template.name
  },
  {
    key: "betbyId",
    label: t("betbyBonuses.templates.columns.betbyId"),
    render: (template) => <CopyBadge value={template.betby_id} />
  },
  {
    key: "type",
    label: t("betbyBonuses.templates.columns.type"),
    render: (template) => (
      <span
        className={clsx(
          "px-2 py-0.5 rounded-full text-xs font-medium",
          template.type === "freebet"
            ? "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100"
            : "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100"
        )}
      >
        {template.type}
      </span>
    )
  },
  {
    key: "subtype",
    label: t("betbyBonuses.templates.columns.subtype"),
    cellClassName: "text-xs text-gray-500",
    render: (template) => {
      if (template.freebet_data) return freebetTypeLabel(t, template.freebet_data.type)
      if (template.comboboost_data?.is_global) return t("betbyBonuses.templates.global")
      return "—"
    }
  },
  {
    key: "validity",
    label: t("betbyBonuses.templates.columns.validity"),
    render: (template) => <DateRange from={template.from_time} to={template.to_time} />
  },
  {
    key: "daysToUse",
    label: t("betbyBonuses.templates.columns.daysToUse"),
    cellClassName: "text-xs text-gray-500",
    render: (template) => template.days_to_use ?? "—"
  },
  {
    key: "maxUses",
    label: t("betbyBonuses.templates.columns.maxUses"),
    cellClassName: "text-xs text-gray-500",
    title: (template) => (template.max_bonus_number === null ? t("betbyBonuses.templates.unlimited") : undefined),
    render: (template) => template.max_bonus_number ?? "∞"
  },
  {
    key: "odds",
    label: t("betbyBonuses.templates.columns.odds"),
    cellClassName: "text-xs text-gray-500 whitespace-nowrap",
    render: (template) => {
      const fd = template.freebet_data
      const cd = template.comboboost_data
      if (fd) return `${fd.min_odd ?? "?"}–${fd.max_odd ?? "?"}`
      if (cd) return t("betbyBonuses.templates.minOdd", { value: cd.min_odd })
      return "—"
    }
  },
  {
    key: "description",
    label: t("betbyBonuses.templates.columns.description"),
    cellClassName: "text-xs text-gray-400 max-w-[160px] truncate",
    title: (template) => getDescription(template) ?? "",
    render: (template) => getDescription(template) ?? <span className="italic text-gray-300">—</span>
  },
  {
    key: "betbyActive",
    label: t("betbyBonuses.templates.columns.betbyActive"),
    render: (template) => (
      <>
        <span className={clsx("inline-block w-2 h-2 rounded-full", template.is_active ? "bg-green-500" : "bg-gray-400")} />
        <span className="ml-1 text-xs text-gray-500">
          {template.is_active ? t("betbyBonuses.templates.yes") : t("betbyBonuses.templates.no")}
        </span>
      </>
    )
  },
  {
    key: "lastSynced",
    label: t("betbyBonuses.templates.columns.lastSynced"),
    render: (template) => <RelativeTime iso={template.last_synced_at} />
  },
  {
    key: "actions",
    label: "",
    render: (template) => (
      <button
        onClick={() => onShowDetails(template)}
        className="px-2 py-0.5 text-xs text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-700 rounded"
      >
        {t("betbyBonuses.templates.details")}
      </button>
    )
  }
]

export function TemplateConfigTable() {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [templates, setTemplates] = useState<BetbyDbTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [detailTemplate, setDetailTemplate] = useState<BetbyDbTemplate | null>(null)

  const columns = buildColumns(t, setDetailTemplate)

  const fetchTemplates = async () => {
    setLoading(true)
    const data = await BetbyBonusService.getTemplates(false)
    setTemplates(data)
    setLoading(false)
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  const handleSync = async () => {
    setSyncing(true)
    const result = await BetbyBonusService.syncTemplates()
    setSyncing(false)
    if (result.synced > 0 || result.created > 0 || result.updated > 0) {
      const message: string = t("betbyBonuses.templates.syncSuccess", {
        synced: result.synced,
        created: result.created,
        updated: result.updated
      })
      toast.success(message)
      fetchTemplates()
    } else {
      const message: string = t("betbyBonuses.templates.syncEmpty")
      toast.info(message)
    }
  }

  return (
    <div>
      {detailTemplate && <DetailsModal template={detailTemplate} onClose={() => setDetailTemplate(null)} />}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t("betbyBonuses.templates.heading")}</h2>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded disabled:opacity-50"
        >
          {syncing ? t("betbyBonuses.templates.syncing") : t("betbyBonuses.templates.sync")}
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 py-8 text-center">{t("betbyBonuses.templates.loading")}</div>
      ) : templates.length === 0 ? (
        <div className="text-gray-500 py-8 text-center">
          <Trans
            i18nKey="betbyBonuses.templates.empty"
            defaults="No templates found. Click <b>Sync from BetBy</b> to import."
            components={{ b: <strong /> }}
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700 text-left text-xs uppercase text-gray-500">
                {columns.map((column, index) => (
                  <th key={column.key} className={clsx("py-2", index < columns.length - 1 && "pr-4")}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      title={column.title?.(template)}
                      className={clsx("py-2", index < columns.length - 1 && "pr-4", column.cellClassName)}
                    >
                      {column.render(template)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
